"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import React, { useCallback, useEffect, useState } from "react"

import { CTAType } from "@/types/ctaCard"
import PageGrid from "@/ui/components/PageGrid"

import CTACard from "./CTACard"

interface CTACardCarouselProps {
  carouselData: CTAType[]
  isCarousel?: boolean
  wrapperClass?: string
  carouselProviderClass?: string
}

const CTACardCarousel: React.FC<CTACardCarouselProps> = ({
  carouselData,
  isCarousel = true,
  wrapperClass,
  carouselProviderClass,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    skipSnaps: false,
    dragFree: false,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  )

  const onInit = useCallback((api: any) => {
    setScrollSnaps(api.scrollSnapList())
  }, [])

  const onSelect = useCallback((api: any) => {
    setSelectedIndex(api.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onInit, onSelect])

  if (!carouselData || carouselData.length === 0) return null

  return (
    <section className={`pt-12 xl:pt-28 lg:pt-20 md:pt-18 ${wrapperClass}`}>
      <PageGrid isGrid={false}>
        <div className={`w-full cta-card-carousel ${carouselProviderClass}`}>
          {/* Embla viewport */}
          <div className="embla" ref={emblaRef}>
            <div className="embla__container">
              {carouselData.map((cardData: any, cardIndex: number) => (
                <div
                  key={cardData.title || `cta-card-${cardIndex}`}
                  className="embla__slide"
                >
                  <div className="min-h-[525px] flex items-center">
                    <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4 md:gap-x-8 lg:gap-x-10 xl:gap-x-12 lg:grid-cols-12 xl:max-w-layout-xl lg:mx-auto lg:max-w-layout-lg w-full">
                      <div className="p-4 col-span-full min-h-[475px] flex items-center">
                        <div className="w-full">
                          <CTACard
                            target={cardData?.target || ""}
                            theme={cardData?.theme}
                            heading={cardData?.heading}
                            copy={cardData?.copy}
                            image={cardData?.image}
                            buttonText={cardData?.buttonText}
                            buttonLink={cardData?.buttonLink}
                            eyebrow={cardData?.eyebrow}
                            isCarousel={isCarousel}
                            backgroundColor="transparent"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots navigation */}
          <PageGrid className="mx-0">
            <div className="p-4 col-span-full">
              <div className="flex items-center justify-center mb-10 -ml-3 cta-dots-container">
                {scrollSnaps.map((snap, index) => (
                  <button
                    key={`dot-${snap}`}
                    onClick={() => scrollTo(index)}
                    className="p-4"
                    type="button"
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    <div
                      className={clsx(
                        "rounded-full mr-8",
                        index === selectedIndex
                          ? "bg-blue-50 w-3 h-3"
                          : "w-2 h-2 bg-gray-60",
                      )}
                    />
                  </button>
                ))}
              </div>
            </div>
          </PageGrid>
        </div>
      </PageGrid>
    </section>
  )
}

export default CTACardCarousel
